import type { TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { EntityContent } from 'src/content/entity-content.js';
import '../../library/editors/index.js';
import '../../library/editors/elements/d-select-tag.js';
import '../../library/elements/d-tooltip.js';
import '../../library/fields/index.js';
import '../../library/lists/d-list-section-attachment.js';
import './d-edit-consequence.js';
import './d-edit-probability.js';
import './d-view-risk.js';
import '../../library/fields/d-view-text.js';
import '../../library/fields/d-view-html.js';
import '../../library/elements/d-action.js';
import '../../library/lists/d-list-section.js';
import '../../library/components/d-popup.js';
import '../../library/fields/d-view-info';
import { t } from 'src/i18n';
import { capitalize, isEmptyHtmlContent, isEmptyOrInvalidString, numberWord, uuid } from 'src/utilities/text.js';
import { LocalDate } from 'src/utilities/local-date.js';
import type { UpdateSectionItem } from '../update-section-item.js';
import type { ListDefinition } from '../list-definition.js';
import type { RiskAssessmentView } from './risk-assessment-view.js';
import type { RiskAssessmentViewEditItem } from './risk-assessment-view-edit-item.js';
import type { SectionField } from 'src/library/components/section-field';
import type { ActionInput } from 'src/library/elements/action-input.js';
import { pageIds } from 'src/store/selectors/pages/pageIds.js';
import type { ListSectionItemInput } from 'src/library/lists/listSectionItemInput.js';
import type { RiskAssessmentListItem } from './risk-assessment-list-item.js';
import type { AddRelatedEntityDialogResult } from 'src/content/add-related-entity-dialog-result.js';
import { AddRelatedEntityDialog } from 'src/content/add-related-entity-dialog.js';
import type { CreateEventOccurrenceInput } from 'src/layout/parts/create-event-occurrence-input.js';
import type { CreateTaskInput } from 'src/layout/parts/create-task-input.js';
import { emergencyCauseCategories } from 'src/store/data/emergency-cause-categories';
import type { RiskAssessmentRelationsChangedDetail } from './risk-assessment-relations-changed-detail.js';
import type { RiskAssessmentMeasuresCardChangedDetail } from './risk-assessment-measures-card-changed-detail.js';
import type { CreateEntityInput } from 'src/layout/parts/create-entity-input';
import suggestedDocumentsList from 'src/store/data/suggested-documents.json';
import type { SelectSuggestionOrEntityDialogResult } from 'src/content/risk-assessments/select-suggestion-or-entity-dialog-result';
import { SelectSuggestionOrEntityDialog } from 'src/content/risk-assessments/select-suggestion-or-entity-dialog';
import type { TaskSummary } from 'src/content/risk-assessments/task-summary';
import type { SuggestedTaskData } from 'src/content/risk-assessments/suggested-task';
import type { PdfField } from 'src/pdf/types';
import { getPdfFieldsForMeasuresCard } from 'src/pdf/measures-card-pdf-fields';

/**
 *
 */
@customElement('d-risk-assessment-view')
export class DRiskAssessmentView extends EntityContent<RiskAssessmentView, RiskAssessmentViewEditItem> {
  static readonly styles = EntityContent.styles.concat(css`
    d-select-radio + d-edit-textarea {
      margin-top: 6px;
    }

    .substance {
      display: block;
      border-top: 1px solid var(--borderColor);
      border-bottom: 1px solid var(--borderColor);
      padding: 12px 0 12px calc(var(--listPaddingLeft) + 12px);
      background-image: url(/images/substances-color.svg);
      background-size: 30px 30px;
      background-position: 10px 8px;
      background-repeat: no-repeat;
      color: var(--themeColorDarkerOne);
      text-decoration: none;
    }

    .dataItem {
      display: block;
      border-top: 1px solid var(--borderColor);
      border-bottom: 1px solid var(--borderColor);
      padding: 12px 0 12px calc(var(--listPaddingLeft) + 12px);
      background-image: url(/images/personal-data-items-color.svg);
      background-size: 30px 30px;
      background-position: 10px 8px;
      background-repeat: no-repeat;
      color: var(--themeColorDarkerOne);
      text-decoration: none;
    }

    @media (hover: hover) {
      .substance:hover {
        color: black;
      }
    }

    .measures {
      margin-top: 8px;
      margin-bottom: 12px;
      border: 2px solid var(--borderColor);
      border-radius: 12px;
      padding: 16px 24px 20px 20px;
    }

    .measures-header {
      display: flex;
      flex-direction: row-reverse;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: baseline;
      padding-bottom: 10px;
    }

    .measures-header > div:last-child {
      flex-grow: 1;
    }

    .measures-header > div {
      display: flex;
      align-items: baseline;
    }

    .measures-header .title {
      font-size: 20px;
      font-weight: 500;
    }

    .measures-header d-action {
      margin-right: -8px;
      margin-left: -6px;
    }

    .suggestions-legend {
      padding: 4px 4px 4px 30px;
      background: url(/images/suggestion-gray.svg) 2px -5px no-repeat;
      background-size: 32px auto;
      font-weight: 200;
    }

    d-list-section:not(.related-assets) {
      margin-bottom: 0;
    }
  `);
  @property({ type: Boolean })
  duplicatable = true;
  @property({ type: Object })
  fieldsByCode = {};
  @property({ type: String })
  defaultHelpForSubstancesRiskAssessment = '';
  @state()
  editingNewRepeated = false;
  @state()
  riskLevelsPopupOpen = false;

  protected get currentItemLabel() {
    let name = this.entityView.name ?? '';
    if (this.entityView.relatedSubstanceName) {
      name = this.entityView.relatedSubstanceName + ': ' + name;
    }
    return name;
  }

  protected get editName() {
    if (this.entityView.relatedSubstanceName) {
      const name = this.entityView.name ?? '';
      return this.entityView.relatedSubstanceName + ': ' + name;
    }
    return super.editName;
  }

  private get measuresCardText() {
    if (!this.entityView.measuresCardPublished) {
      return t('riskAssessments.measuresCardNotPublished');
    }
    return t('riskAssessments.measuresCardPublished', {
      date: LocalDate.fromString(this.entityView.measuresCardPublished).toStringForDisplay(),
    });
  }

  renderRevisionItems() {
    return html``;
  }

  async onDone() {
    this.editingNewRepeated = false;
    return super.onDone();
  }

  private get suggestedEvents(): RiskAssessmentListItem[] {
    if (this.entityView.emergencyContent && !this.entityView.currentUserHasEmergencyAccess) {
      return [];
    }
    return this.entityView.suggestedEvents.map((item) => {
      const template = suggestedDocumentsList.tasks.find((t) => t.pageId === (item.templateId ?? 0));
      if (template) {
        return {
          ...item,
          clickHandler: () => {
            const eventsFromSuggestions = this.entityView.eventsFromSuggestions.filter(
              (t) => t.createdFromTemplate === item.templateId,
            );
            if (eventsFromSuggestions.length) {
              const suggestionSummary = this.entityView.suggestedTaskSummaries.find(
                (s) => s.templateId === item.templateId,
              );
              if (suggestionSummary) {
                return this.onPreviouslyUsedSuggestionClick(
                  'eventOccurrences',
                  suggestionSummary,
                  eventsFromSuggestions,
                  item,
                  template,
                );
              }
            } else {
              this.createEventFromSuggestion(item, template);
            }
          },
        };
      }
      return item;
    });
  }

  private get eventsForList(): RiskAssessmentListItem[] {
    const relatedEventsTemplateIds = this.entityView.relatedEvents.map((item) => item.createdFromTemplate ?? 0);
    const relatedEvents: RiskAssessmentListItem[] = this.entityView.relatedEvents.map((item) => ({
      ...item,
      clickData: undefined, // ClickData prevents link behaviour
    }));
    const suggestedEvents = this.suggestedEvents.filter(
      (item) => !relatedEventsTemplateIds.includes(item.templateId ?? 0),
    );
    return relatedEvents.concat(suggestedEvents);
  }

  private async onPreviouslyUsedSuggestionClick(
    entityType: 'eventOccurrences' | 'tasks',
    suggestion: TaskSummary,
    tasksFromSuggestion: TaskSummary[],
    item: RiskAssessmentListItem,
    template: SuggestedTaskData,
  ) {
    const result: SelectSuggestionOrEntityDialogResult = await SelectSuggestionOrEntityDialog.open({
      riskAssessmentName: this.entityView.name,
      entityType,
      entities: tasksFromSuggestion,
      suggestion,
    });
    if (result.action === 'done') {
      if (result.uuid === undefined) {
        if (entityType === 'tasks') {
          this.createTaskFromSuggestion(item, template);
        }
        if (entityType === 'eventOccurrences') {
          this.createEventFromSuggestion(item, template);
        }
      } else {
        this.addEntities(entityType, [result.uuid]);
      }
    }
  }

  private createEventFromSuggestion(item: RiskAssessmentListItem, template: SuggestedTaskData) {
    const id = uuid();
    const relatedEventUuids = this.entityView.relatedEvents
      .filter((re) => !re.suggestion && re.href)
      .map((rt) => rt.href?.split('/').at(-1) ?? '');
    relatedEventUuids.push(id);
    const input: CreateEventOccurrenceInput = {
      entityType: 'eventOccurrences',
      entityUuid: id,
      relatedRiskAssessment: {
        riskAssessmentId: this.entityView.uuid,
        uuids: relatedEventUuids,
      },
      targetUrl: this.entityView.href + '/eventOccurrences/' + id + '?edit&newEventFromSuggestion=true',
      // Prefill the new emergency task from the suggestion template (#13169).
      createdFromTemplate: template.mainVersionPageId !== 0 ? template.mainVersionPageId : template.pageId,
      name: template.name,
      description: template.procedures,
      functionUuid: item.functionUuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private createTaskFromSuggestion(item: RiskAssessmentListItem, template: SuggestedTaskData) {
    const id = uuid();
    const relatedTaskUuids = this.entityView.relatedTasks
      .filter((rt) => !rt.suggestion && rt.href)
      .map((rt) => rt.href?.split('/').at(-1) ?? '');
    relatedTaskUuids.push(id);
    const input: CreateTaskInput = {
      entityType: 'tasks',
      entityUuid: id,
      pageId: pageIds.emergency,
      functionId: item.functionUuid ?? '',
      relatedRiskAssessment: {
        riskAssessmentId: this.entityView.uuid,
        uuids: relatedTaskUuids,
      },
      targetUrl: this.entityView.href + '/tasks/' + id + '?edit',
      // Prefill the new emergency routine from the suggestion template (#13169).
      // createdFromTemplate lagres i eget Task.templateId-felt (trinnvis/dabih-api#4635),
      // atskilt fra Task.pageId – ingen konflatering/skjul-risiko.
      createdFromTemplate: template.mainVersionPageId !== 0 ? template.mainVersionPageId : template.pageId,
      name: template.name,
      procedures: template.procedures,
      responsibleFunctionUuid: item.responsibleFunctionUuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private get suggestedTasks(): RiskAssessmentListItem[] {
    if (this.entityView.emergencyContent && !this.entityView.currentUserHasEmergencyAccess) {
      return [];
    }
    return this.entityView.suggestedTasks.map((item) => {
      const template: SuggestedTaskData | undefined = suggestedDocumentsList.tasks.find(
        (t) => t.pageId === (item.templateId ?? 0),
      );
      if (template) {
        return {
          ...item,
          clickHandler: () => {
            const tasksFromSuggestion = this.entityView.tasksFromSuggestions.filter(
              (t) => t.createdFromTemplate === item.templateId,
            );
            if (tasksFromSuggestion.length) {
              const suggestionSummary = this.entityView.suggestedTaskSummaries.find(
                (s) => s.templateId === item.templateId,
              );
              if (suggestionSummary) {
                return this.onPreviouslyUsedSuggestionClick(
                  'tasks',
                  suggestionSummary,
                  tasksFromSuggestion,
                  item,
                  template,
                );
              }
            }
            this.createTaskFromSuggestion(item, template);
          },
        };
      }
      return item;
    });
  }

  private get tasksForList(): RiskAssessmentListItem[] {
    const relatedTasksTemplateIds = this.entityView.relatedTasks.map((item) => item.createdFromTemplate ?? 0);
    const relatedTasks: RiskAssessmentListItem[] = this.entityView.relatedTasks.map((item) => ({
      ...item,
      clickData: undefined, // ClickData prevents link behaviour
    }));
    const suggestedTasks = this.suggestedTasks.filter(
      (item) => !relatedTasksTemplateIds.includes(item.templateId ?? 0),
    );
    return relatedTasks.concat(suggestedTasks);
  }

  fireRelationsChanged(entityType: 'eventOccurrences' | 'tasks', uuids: string[]) {
    // Persist the full desired set (set-replace) — handled at app level where
    // the API client lives. trinnvis/dabih-api#4573.
    this.dispatchEvent(
      new CustomEvent<RiskAssessmentRelationsChangedDetail>('risk-assessment-relations-changed', {
        bubbles: true,
        composed: true,
        detail: {
          riskAssessmentId: this.entityView.uuid,
          entityType,
          uuids,
        },
      }),
    );
  }

  addEntities(entityType: 'eventOccurrences' | 'tasks', entities: string[]) {
    let uuids: string[] = [];
    if (entityType === 'eventOccurrences') {
      const listItems = this.entityView.availableEvents.filter((item) => entities.includes(item.entityUuid ?? ''));
      const relatedEvents = [...this.entityView.relatedEvents, ...listItems];
      this.entityView = { ...this.entityView, relatedEvents };
      uuids = relatedEvents.map((item) => item.entityUuid).filter((id): id is string => !!id);
    }
    if (entityType === 'tasks') {
      const newItems = this.entityView.availableTasks.filter((item) => entities.includes(item.entityUuid ?? ''));
      const relatedTasks = [...this.entityView.relatedTasks, ...newItems];
      this.entityView = { ...this.entityView, relatedTasks };
      uuids = relatedTasks.map((item) => item.entityUuid).filter((id): id is string => !!id);
    }
    this.fireRelationsChanged(entityType, uuids);
  }

  removeEntity(e: CustomEvent<ListSectionItemInput>, entityType: 'eventOccurrences' | 'tasks') {
    if (e.detail.href) {
      let uuids: string[] = [];
      const removed = e.detail.href.split('/').at(-1);
      if (entityType === 'eventOccurrences') {
        const relatedEvents = this.entityView.relatedEvents.filter((item) => item.entityUuid !== removed);
        this.entityView = { ...this.entityView, relatedEvents };
        uuids = relatedEvents.map((item) => item.entityUuid).filter((id): id is string => !!id);
      }
      if (entityType === 'tasks') {
        const relatedTasks = this.entityView.relatedTasks.filter((item) => item.entityUuid !== removed);
        this.entityView = { ...this.entityView, relatedTasks };
        uuids = relatedTasks.map((item) => item.entityUuid).filter((id): id is string => !!id);
      }
      this.fireRelationsChanged(entityType, uuids);
    }
  }

  async onFindEntity(entityType: 'eventOccurrences' | 'tasks') {
    let availableEntities: ListSectionItemInput[] = [];
    if (entityType === 'eventOccurrences') {
      availableEntities = this.entityView.availableEvents.map((item) => ({ ...item, clickData: item.entityUuid }));
    }
    if (entityType === 'tasks') {
      availableEntities = this.entityView.availableTasks.map((item) => ({ ...item, clickData: item.entityUuid }));
    }
    const result: AddRelatedEntityDialogResult = await AddRelatedEntityDialog.open({
      entityType,
      availableEntities,
    });
    if (result.action === 'done') {
      this.addEntities(entityType, result.selectedEntities);
    }
  }

  async onCreateEntity(entityType: 'eventOccurrences' | 'tasks') {
    const id = uuid();
    // The new entity is linked to this risk assessment once it has been
    // persisted (set-replace, handled at app level after creation). The full
    // desired set = existing relation uuids + the new one. trinnvis/dabih-api#4573.
    const existing = (entityType === 'eventOccurrences' ? this.entityView.relatedEvents : this.entityView.relatedTasks)
      .map((item) => item.entityUuid)
      .filter((relationId): relationId is string => !!relationId);
    const relatedRiskAssessment = {
      riskAssessmentId: this.entityView.uuid,
      uuids: [...existing, id],
    };
    if (entityType === 'eventOccurrences') {
      this.dispatchEvent(
        new CustomEvent<CreateEventOccurrenceInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: {
            entityType: 'eventOccurrences',
            entityUuid: id,
            targetUrl: this.entityView.href + '/eventOccurrences/' + id + '?edit',
            relatedRiskAssessment,
          },
        }),
      );
    }
    if (entityType === 'tasks') {
      this.dispatchEvent(
        new CustomEvent<CreateTaskInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: {
            entityType: 'tasks',
            entityUuid: id,
            targetUrl: this.entityView.href + '/tasks/' + id + '?edit',
            functionId: this.entityView.defaultFunctionUuid,
            pageId: pageIds.management,
            relatedRiskAssessment,
          },
        }),
      );
    }
  }

  onListAction(e: CustomEvent<string>): Promise<void> | undefined {
    if (e.detail === 'find-event') {
      return this.onFindEntity('eventOccurrences');
    }
    if (e.detail === 'find-task') {
      return this.onFindEntity('tasks');
    }
    if (e.detail === 'create-event') {
      return this.onCreateEntity('eventOccurrences');
    }
    if (e.detail === 'create-task') {
      return this.onCreateEntity('tasks');
    }
  }

  onMeasuresCardPublish(measuresCardDisabled: boolean) {
    if (!measuresCardDisabled) {
      const published = !this.entityView.measuresCardPublished;
      // Optimistisk: server setter autoritativ dato, men vis umiddelbart.
      this.entityView = {
        ...this.entityView,
        measuresCardPublished: published ? LocalDate.now().toString() : undefined,
      };
      // Persisteres via dedikert publiser/avpubliser-endepunkt (app-nivå).
      // trinnvis/dabih-api#4577.
      this.dispatchEvent(
        new CustomEvent<RiskAssessmentMeasuresCardChangedDetail>('risk-assessment-measures-card-changed', {
          bubbles: true,
          composed: true,
          detail: {
            riskAssessmentId: this.entityView.uuid,
            published,
          },
        }),
      );
    }
  }

  private get suggestionsCount() {
    return [...this.eventsForList, ...this.tasksForList].filter((item) => item.suggestion).length;
  }

  private get suggestionLegendText(): string {
    if (this.suggestionsCount === 1) {
      return t('riskAssessments.unusedSuggestion');
    }
    return t('riskAssessments.unusedSuggestions', { count: capitalize(numberWord(this.suggestionsCount)) });
  }

  renderMeasures() {
    const measuresCardDisabled = this.entityView.relatedTasks.filter((m) => m.mainIcon !== 'suggestion').length === 0;
    let eventListActions: ActionInput[] = [];
    let taskListActions: ActionInput[] = [];
    if (!this.entityView.emergencyContent || this.entityView.currentUserHasEmergencyAccess) {
      eventListActions = [
        { name: t('riskAssessments.createTask'), action: 'create-event', slot: 'top-right', plain: true },
        { name: t('riskAssessments.findTask'), action: 'find-event', slot: 'top-right', plain: true },
      ];
      taskListActions = [
        { name: t('riskAssessments.createRoutine'), action: 'create-task', slot: 'top-right', plain: true },
        { name: t('riskAssessments.findRoutine'), action: 'find-task', slot: 'top-right', plain: true },
      ];
    }
    return html`
      <div class="measures">
        <div class="measures-header">
          <div>
            ${this.entityView.featureStates.emergency && this.entityView.pages.includes(pageIds.emergency + '')
              ? html`
                  <d-view-text
                    system-content
                    inline-label
                    field="riskAssessments_measuresCard"
                    .sublabel=${this.measuresCardText}
                  ></d-view-text>
                  ${!this.entityView.emergencyContent || this.entityView.currentUserHasEmergencyAccess
                    ? html`
                        <d-action
                          plain
                          ?disabled="${measuresCardDisabled}"
                          @click=${() => this.onMeasuresCardPublish(measuresCardDisabled)}
                          >${!this.entityView.measuresCardPublished
                            ? t('riskAssessments.publish')
                            : t('riskAssessments.unpublish')}
                        </d-action>
                      `
                    : nothing}
                `
              : nothing}
          </div>
          <div>
            <div class="title">${t('riskAssessments.measures')}</div>
            ${this.suggestionsCount > 0
              ? html` <div class="suggestions-legend">${this.suggestionLegendText}</div> `
              : nothing}
          </div>
        </div>
        <d-list-section
          bordered
          label="${t('riskAssessments.beforeItHappens')}"
          icon="events"
          .items=${this.eventsForList}
          no-item-text="${t('riskAssessments.noMeasures')}"
          .actions=${eventListActions}
          @action=${(e: CustomEvent<string>) => this.onListAction(e)}
          @remove-item=${(e: CustomEvent<ListSectionItemInput>) => this.removeEntity(e, 'eventOccurrences')}
        ></d-list-section>
        <d-list-section
          bordered
          label="${t('riskAssessments.ifItHappens')}"
          icon="tasks"
          .items=${this.tasksForList}
          no-item-text="${t('riskAssessments.noMeasures')}"
          .actions=${taskListActions}
          @action=${(e: CustomEvent<string>) => this.onListAction(e)}
          @remove-item=${(e: CustomEvent<ListSectionItemInput>) => this.removeEntity(e, 'tasks')}
        ></d-list-section>
      </div>
    `;
  }

  renderViewMode() {
    return html`${this.renderFieldSection('riskAssessments', this.viewFields(), this.entityView)}
    ${this.renderMeasures()}`;
  }

  async initializeEditItem() {
    let assessmentDate = this.entityView.assessmentDate;
    if (assessmentDate === '') {
      assessmentDate = LocalDate.now().toString();
    }
    this.editItem = {
      assessmentDate,
      consequence: this.entityView.consequence,
      description: this.entityView.description,
      measures: this.entityView.measures,
      // Behold eksisterende verdier til backend-migreringen (dabih-api#4502) folder
      // dem inn i measures; ikke nullstill (ville tapt data ved redigering før migrering).
      pages: this.entityView.pages,
      probability: this.entityView.probability,
      probabilityDescription: this.entityView.probabilityDescription,
      riskDescription: this.entityView.riskDescription,
      violatesAvailabilityRequirement: this.entityView.violatesAvailabilityRequirement,
      violatesConfidentialityRequirement: this.entityView.violatesConfidentialityRequirement,
      violatesInformationSecurityRequirements: this.entityView.violatesInformationSecurityRequirements,
      violatesIntegrityRequirement: this.entityView.violatesIntegrityRequirement,
      violatesNonRepudiationRequirement: this.entityView.violatesNonRepudiationRequirement,
      name: this.entityView.name,
      createdFromTemplate: this.entityView.createdFromTemplate,
      relatedSubstance: this.entityView.relatedSubstance,
      relatedDataItem: this.entityView.relatedDataItem,
      causeCategories: this.entityView.causeCategories,
    };
  }

  doneDisabled(): boolean {
    return isEmptyOrInvalidString(this.editItem?.name);
  }

  renderPreContent() {
    if (this.entityView.substanceRelated) {
      return html` <d-section
        ><a class="substance" href="${this.entityView.relatedSubstanceHref}"
          >${this.entityView.relatedSubstanceName}</a
        ></d-section
      >`;
    }
    if (this.entityView.dataItemRelated) {
      return html` <d-section
        ><a class="dataItem" href="${this.entityView.relatedDataItemHref}"
          >${this.entityView.relatedDataItemName}</a
        ></d-section
      >`;
    }
    return html``;
  }

  async repeatRiskAssessment() {
    this.editingNewRepeated = true;
    const editItem: RiskAssessmentViewEditItem = {
      name: this.entityView.name,
      assessmentDate: LocalDate.now().toString(),
      consequence: 0,
      description: this.entityView.description,
      measures: '',
      pages: this.entityView.pages,
      probability: 0,
      probabilityDescription: '',
      riskDescription: this.entityView.riskDescription,
      violatesAvailabilityRequirement: this.entityView.violatesAvailabilityRequirement,
      violatesConfidentialityRequirement: this.entityView.violatesConfidentialityRequirement,
      violatesInformationSecurityRequirements: this.entityView.violatesInformationSecurityRequirements,
      violatesIntegrityRequirement: this.entityView.violatesIntegrityRequirement,
      violatesNonRepudiationRequirement: this.entityView.violatesNonRepudiationRequirement,
      createdFromTemplate: this.entityView.createdFromTemplate,
      relatedSubstance: this.entityView.relatedSubstance,
      relatedDataItem: this.entityView.relatedDataItem,
      causeCategories: this.entityView.causeCategories,
    };
    this.dispatchEvent(
      new CustomEvent('save-as-new', {
        bubbles: true,
        composed: true,
        detail: {
          editItem,
        },
      }),
    );
  }

  renderEditMeasures(item: RiskAssessmentViewEditItem): TemplateResult<1> {
    if (isEmptyHtmlContent(this.entityView.measures)) {
      return html``;
    }
    return html`
      <d-section>
        <d-edit-textarea
          field="riskAssessments_measuresDescription"
          .value=${item.measures}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, measures: e.detail.value };
          }}
        >
        </d-edit-textarea>
      </d-section>
    `;
  }

  renderEditItem(item: RiskAssessmentViewEditItem): TemplateResult<1> {
    return html`
      ${(item.createdFromTemplate === undefined || item.createdFromTemplate === null) && !this.editingNewRepeated
        ? html` <d-section>
            <d-edit-text
              field="riskAssessments_name"
              .autofocus=${!this.entityView.isConfirmedEntity}
              .selectOnFocus=${!this.entityView.isConfirmedEntity}
              placeholder="${t('riskAssessments.requiredField')}"
              mark-if-empty
              .value=${item.name}
              @value-changed=${(e: CustomEvent<{ value: string }>) => {
                this.editItem = { ...item, name: e.detail.value };
              }}
            >
            </d-edit-text>
          </d-section>`
        : html` <d-section>
            <d-view-text field="riskAssessments_name" .value=${this.entityView.name}></d-view-text>
          </d-section>`}

      <d-section>
        <d-select-date
          field="riskAssessments_assessmentDate"
          .value=${item.assessmentDate}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, assessmentDate: e.detail.value };
          }}
        >
        </d-select-date>
      </d-section>

      <d-section>
        <d-edit-html
          wrap-label
          class="fullWidth"
          field="riskAssessments_description"
          sublabel="${t('riskAssessments.descriptionSublabel')}"
          .value=${item.description}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            this.editItem = { ...item, description: e.detail.value };
          }}
        >
        </d-edit-html>
        ${this.editItem?.pages.includes(pageIds.emergency + '')
          ? html`
              <d-select-tag
                field="riskAssessments_causeCategories"
                .options=${emergencyCauseCategories}
                .value=${item.causeCategories}
                @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
                  this.editItem = { ...item, causeCategories: e.detail.value };
                }}
              ></d-select-tag>
            `
          : nothing}
      </d-section>

      ${!this.entityView.featureStates.data || this.entityView.substanceRelated
        ? nothing
        : html` <div>
            <d-section>
              <d-checkbox
                ?checked=${item.violatesInformationSecurityRequirements}
                option-field="riskAssessments_violatesInformationSecurityRequirements"
                @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                  this.editItem = { ...item, violatesInformationSecurityRequirements: e.detail.checked };
                }}
              >
              </d-checkbox>
            </d-section>

            ${item.violatesInformationSecurityRequirements
              ? html` <d-section field="riskAssessments_informationSecurityDescription">
                  <d-checkbox
                    ?checked=${item.violatesConfidentialityRequirement}
                    option-field="riskAssessments_violatesConfidentialityRequirement"
                    @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                      this.editItem = { ...item, violatesConfidentialityRequirement: e.detail.checked };
                    }}
                  >
                  </d-checkbox>
                  <d-checkbox
                    ?checked=${item.violatesIntegrityRequirement}
                    option-field="riskAssessments_violatesIntegrityRequirement"
                    @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                      this.editItem = { ...item, violatesIntegrityRequirement: e.detail.checked };
                    }}
                  >
                  </d-checkbox>
                  <d-checkbox
                    ?checked=${item.violatesAvailabilityRequirement}
                    option-field="riskAssessments_violatesAvailabilityRequirement"
                    @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                      this.editItem = { ...item, violatesAvailabilityRequirement: e.detail.checked };
                    }}
                  >
                  </d-checkbox>
                  <d-checkbox
                    ?checked=${item.violatesNonRepudiationRequirement}
                    option-field="riskAssessments_violatesNonRepudiationRequirement"
                    @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                      this.editItem = { ...item, violatesNonRepudiationRequirement: e.detail.checked };
                    }}
                  >
                  </d-checkbox>
                </d-section>`
              : nothing}
          </div>`}

      <d-section>
        <div>
          <d-select-radio
            inline-label
            event-on-tooltip
            class="fullWidth"
            field="riskAssessments_probability"
            .options=${[
              { value: '1', text: t('riskAssessments.probabilityLow'), color: 'var(--risk-color-1)' },
              { value: '2', text: t('riskAssessments.probabilityMedium'), color: 'var(--risk-color-2)' },
              { value: '3', text: t('riskAssessments.probabilityHigh'), color: 'var(--risk-color-3)' },
              { value: '4', text: t('riskAssessments.probabilityVeryHigh'), color: 'var(--risk-color-4)' },
            ]}
            .value=${String(item.probability)}
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.editItem = { ...item, probability: Number(e.detail.value) };
            }}
            @tooltip-clicked=${() => (this.riskLevelsPopupOpen = true)}
          ></d-select-radio>
          <d-edit-textarea
            class="fullWidth"
            light-label
            .value=${item.probabilityDescription}
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.editItem = { ...item, probabilityDescription: e.detail.value };
            }}
          >
          </d-edit-textarea>
        </div>
        <div>
          <d-select-radio
            inline-label
            event-on-tooltip
            class="fullWidth"
            field="riskAssessments_consequence"
            .options=${[
              { value: '1', text: t('riskAssessments.consequenceNegligible'), color: 'var(--risk-color-1)' },
              { value: '2', text: t('riskAssessments.consequenceModerate'), color: 'var(--risk-color-2)' },
              { value: '3', text: t('riskAssessments.consequenceSerious'), color: 'var(--risk-color-3)' },
              { value: '4', text: t('riskAssessments.consequenceCatastrophic'), color: 'var(--risk-color-4)' },
            ]}
            .value=${String(item.consequence)}
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.editItem = { ...item, consequence: Number(e.detail.value) };
            }}
            @tooltip-clicked=${() => (this.riskLevelsPopupOpen = true)}
          ></d-select-radio>
          <d-edit-textarea
            class="fullWidth"
            light-label
            .value=${item.riskDescription}
            @value-changed=${(e: CustomEvent<{ value: string }>) => {
              this.editItem = { ...item, riskDescription: e.detail.value };
            }}
          >
          </d-edit-textarea>
        </div>
      </d-section>

      <d-section>
        <d-view-risk .value=${this.riskValue(item.consequence, item.probability)}></d-view-risk>
      </d-section>

      ${this.renderEditMeasures(item)}

      <d-popup
        .show=${this.riskLevelsPopupOpen}
        @close=${(e) => {
          e.stopPropagation();
          this.riskLevelsPopupOpen = false;
        }}
      >
        <d-view-info
          .content=${this.entityView.riskLevels}
          @update-help=${() => (this.riskLevelsPopupOpen = false)}
        ></d-view-info>
      </d-popup>
    `;
  }

  protected viewFields(): SectionField[] {
    return [
      {
        field: 'calculatedRisk',
        type: 'view-risk-simple',
        class: 'fullWidth',
        excludeFromPdf: true,
      },
      {
        field: 'assessmentDate',
        type: 'view-date',
        format: 'date-short',
        class: 'fullWidth',
        condition: this.entityView.assessmentDate !== '',
      },
      {
        field: 'description',
        type: 'view-html',
        class: 'fullWidth',
        condition: !isEmptyHtmlContent(this.entityView.description),
      },
      {
        field: 'causeCategoriesDisplay',
        fieldForLabel: 'causeCategories',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.causeCategories.length > 0,
      },
      {
        field: 'informationSecurityDescription',
        type: 'view-text',
        class: 'fullWidth',
        condition:
          this.entityView.featureStates.data &&
          !this.entityView.substanceRelated &&
          this.entityView.violatesInformationSecurityRequirements,
      },
      {
        field: 'probabilityDescription',
        fieldForLabel: 'probabilityDescription',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.probabilityDescription !== '',
      },
      {
        field: 'riskDescription',
        fieldForLabel: 'consequenceDescription',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.riskDescription !== '',
      },
      {
        field: 'calculatedRiskDisplayText',
        fieldForLabel: 'calculatedRisk',
        type: 'view-text',
        class: 'fullWidth',
        condition: false,
      },
      {
        field: 'measures',
        fieldForLabel: 'measuresDescription',
        type: 'view-text',
        class: 'fullWidth',
        condition: this.entityView.measures !== '',
      },
    ];
  }

  protected lists(): ListDefinition[] {
    return [
      {
        field: 'riskAssessments_previousAssessments',
        icon: 'riskAssessments',
        items: 'previousAssessments',
        condition: this.entityView.previousAssessments.length > 0,
      },
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected renderEditSelectCategories(item: any): TemplateResult<1> {
    if (this.editItem) {
      return html`
        <d-section>
          <d-select-tag
            label="${t('riskAssessments.category')}"
            .options=${this.entityView.availableCategories}
            .value=${item.pages}
            @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
              this.editItem = { ...item, pages: e.detail.value };
            }}
          ></d-select-tag>
        </d-section>
      `;
    }
    return html``;
  }

  protected renderEditSelectPages(item: any): TemplateResult<1> {
    if (this.entityView.isNewest && !this.editingNewRepeated) {
      if (
        (!this.entityView.availablePages || this.entityView.availablePages.length === 0) &&
        !this.entityView.substanceRelated
      ) {
        return this.renderEditSelectCategories(item);
      }
      return super.renderEditSelectPages(item);
    }
    return html``;
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return this.entityView;
  }

  private riskValue(consequence: number, probability: number): number {
    return consequence * probability;
  }

  protected pdfFieldsForMeasuresCard(): PdfField[] {
    return getPdfFieldsForMeasuresCard(
      this.entityView.relatedTasksForPdf,
      this.entityView.functionNames,
      this.entityView.partnersAndPeopleWithContactInfo,
      this.entityView.docsForLinking ?? [],
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-risk-assessment-view': DRiskAssessmentView;
  }
}
